import type { UIDebugPageLink } from '@sorrem-client/types';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { TabDef } from '@sorrem-client/ui/components';
import { CharacterHomeHeader } from '@sorrem-client/ui/features/CharacterHome';
import { useAppNavigate, useDeveloperAccess, useEditorAccess } from '@sorrem-client/ui/hooks';
import { useGetAuthSessionQuery } from '@sorrem-client/ui/store';
import styles from './DeveloperPageLayout.module.scss';

interface DeveloperPageLayoutProps {
    /** The set of pages this one belongs to, one tab each: the developer pages or the editors. */
    pages: readonly UIDebugPageLink[];
    /** Ends the session; resolves once it is cleared. */
    onLogout: () => Promise<void>;
    /** The page shown below the header. */
    children: ReactNode;
}

/**
 * Frame for the role-gated pages (the /debug/* tools and the lore, or the
 * /editors/* pages): Character Home's header on top, its tab strip listing
 * the pages of this one's set (the current one selected) and its title and
 * account menu leading back to the rest of the app, and the page filling the
 * space below.
 */
export function DeveloperPageLayout({ pages, onLogout, children }: DeveloperPageLayoutProps): JSX.Element {
    const navigate = useAppNavigate();
    const { data: authSession } = useGetAuthSessionQuery();
    const { isDeveloper, debugPages } = useDeveloperAccess();
    const { isCreator, editorPages } = useEditorAccess();
    const { pathname } = useLocation();

    // Each page of the set is a tab, keyed by its path.
    const pageTabs: TabDef<string>[] = useMemo(
        () => pages.map((page) => ({ id: page.path, label: page.tabLabel })),
        [pages],
    );
    // Sub-routes (/lore/:slug) keep their page's tab selected.
    const activeTab = pages.find((page) => pathname === page.path || pathname.startsWith(`${page.path}/`))?.path ?? null;

    const handleTabChange = useCallback((path: string): void => {
        navigate(path);
    }, [navigate]);

    const handleLogout = useCallback((): void => {
        void onLogout().then(() => navigate('/login'));
    }, [navigate, onLogout]);

    return (
        <div className={styles.developerPageLayout}>
            <CharacterHomeHeader
                accountProfile={authSession ?? null}
                isDeveloper={isDeveloper}
                debugPages={debugPages}
                isCreator={isCreator}
                editorPages={editorPages}
                onNavigate={navigate}
                onLogout={handleLogout}
                tabs={pageTabs}
                tabSizing="content"
                activeTab={activeTab}
                onTabChange={handleTabChange}
                areTabsShown
                className={styles.header}
            />
            <main className={styles.content}>{children}</main>
        </div>
    );
}
